import React, { useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import "./Header.css";
import { IconMenu, LogoRefabric } from "icons";
import Button from "components/Button/Button";
import clsx from "clsx";
import AvatarMenu from "components/AvatarMenu/AvatarMenu";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setUser, toggleWebinarModal } from "store/commonSlice";
import { useAppSelector } from "store";
import userService from "api/user/user.service";
import { useIsMobile } from "hooks/useIsMobile";
import { AnalyticsEvent } from "utils";
import { PATHS } from "../../../router/config/paths";
import { BASIC_APP_CREATE_URL, CLIENT_ID_LOCAL_STORAGE_KEY, DOCS_URL } from "utils/constants";
import { buildAppUrl } from "utils/buildAppUrl";
import { getAuthTokenFromLocalStorage } from "hooks/useAuthToken";
import { useLocalStorage } from "hooks/useLocalStorage";
import { useClickOutside } from "hooks/useClickOutside";
import MobileHeaderMenu from "./MobileHeaderMenu";
import AnnouncementModal from "./AnnouncementModal";
import WebinarModal from "./WebinarModal";
import { ImageAIFabric_3, ImageAIFabric_4, ImageAP_OnBody1, ImageAP_OnBody2, ImageTPSlider1, ImageTPSlider2, ImageAmbasadors1, ImageContactUsHero1 } from "images";
import BeforeAfterImage from "pages/FeaturesPage/components/BeforeAfterImage";
import "pages/FeaturesPage/components/DesignTools.css";

export enum PageType {
  Explore = "/",
  Create = "/create",
  Pricing = "/pricing",
}

const Header = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);
  const [initTab, setInitTab] = React.useState<any>(location.pathname);
  const { isAuthorized, user, isCheckingToken, presentationMode, presentationInstant } = useAppSelector((state) => state.common);
  const [showProductsSelect, setShowProductsSelect] = useState(false);
  const productsSelectRef = useRef(null);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showPremiereVisionModal, setShowPremiereVisionModal] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openDropdownIndex, setOpenDropdownIndex] = useState<number | null>(null);
  const [dropdownTop, setDropdownTop] = useState(96);
  const [dropdownLeft, setDropdownLeft] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const headerRef = useRef<HTMLDivElement>(null);

  const openDropdown = (index: number, triggerLeft?: number) => {
    if (dropdownCloseTimer.current) clearTimeout(dropdownCloseTimer.current);
    if (headerRef.current) {
      setDropdownTop(headerRef.current.getBoundingClientRect().bottom + 8);
    }
    if (triggerLeft !== undefined) {
      setDropdownLeft(triggerLeft);
    }
    setOpenDropdownIndex(index);
  };

  const closeDropdown = () => {
    dropdownCloseTimer.current = setTimeout(() => setOpenDropdownIndex(null), 120);
  };

  useClickOutside(productsSelectRef, () => setShowProductsSelect(false));

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  React.useEffect(() => {
    if (location.pathname !== initTab) {
      setInitTab(location.pathname);
    }
  }, [location.pathname]);

  React.useEffect(() => {
    if (user.id !== "") {
      userService
        .getUserInfo({ uid: user?.id })
        .then((res: any) => dispatch(setUser({ id: res.user_info.id, email: res.user_info.email, name: res.user_info.name, username: res.user_info.username, work: res.user_info.work })));
    }
  }, [isAuthorized]);

  React.useEffect(() => {
    setShowPremiereVisionModal(true);
    dispatch(toggleWebinarModal());
  }, []);

  const toggleDropdown = () => {
    AnalyticsEvent("profile_tapped", "", "click");
    setIsOpen(!isOpen);
  };

  const HeaderItems: any[] = [
    {
      title: "Features",
      renderItem: ({ setShowRenderItem, dropdownTop: top = 96, onPanelMouseEnter, onPanelMouseLeave }: any) => {
        const goToFeatures = () => {
          AnalyticsEvent("header_features", "", "click");
          navigate(PATHS.FEATURES + window.location.search);
          setShowRenderItem(false);
        };
        const goToPhotoshoot = () => {
          AnalyticsEvent("header_ai_photoshoot", "", "click");
          navigate(PATHS.AI_PHOTOSHOOT + window.location.search);
          setShowRenderItem(false);
        };
        const goToTechPacks = () => {
          AnalyticsEvent("header_tech_packs", "", "click");
          navigate(PATHS.TECH_PACKS + window.location.search);
          setShowRenderItem(false);
        };

        return (
          <div
            className="flex gap-5 p-6 rounded-2xl w-[900px]"
            style={{
              position: "fixed",
              top,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 100,
              background: "rgba(242, 242, 247, 0.72)",
              backdropFilter: "saturate(180%) blur(40px)",
              WebkitBackdropFilter: "saturate(180%) blur(40px)",
            }}
            onMouseEnter={onPanelMouseEnter}
            onMouseLeave={onPanelMouseLeave}
          >
            <div className="flex flex-col gap-4 w-full">
              <div className="w-full aspect-square rounded-md overflow-hidden">
                <BeforeAfterImage fill sliderPercentPosition={50} mainImage={ImageAIFabric_4} images={[ImageAIFabric_3]} />
              </div>
              <span className="text-h7-satoshi text-black">AI Design</span>
              <span className="text-bodyLg text-black">Design faster and smarter with AI, from first idea to final look.</span>
              <Button className="btn-cta w-fit mt-auto" onClick={goToFeatures}>
                Explore
              </Button>
            </div>
            <div className="flex flex-col gap-4 w-full">
              <div className="w-full aspect-square rounded-md overflow-hidden">
                <BeforeAfterImage fill sliderPercentPosition={50} mainImage={ImageAP_OnBody1} images={[ImageAP_OnBody2]} />
              </div>
              <span className="text-h7-satoshi text-black">AI Photoshoot</span>
              <span className="text-bodyLg text-black">Create e-commerce-ready visuals without a studio, samples, or models.</span>
              <Button className="btn-cta w-fit mt-auto" onClick={goToPhotoshoot}>
                Explore
              </Button>
            </div>
            <div className="flex flex-col gap-4 w-full">
              <div className="w-full aspect-square rounded-md overflow-hidden">
                <BeforeAfterImage fill sliderPercentPosition={50} mainImage={ImageTPSlider1} images={[ImageTPSlider2]} />
              </div>
              <span className="text-h7-satoshi text-black">AI Tech Packs</span>
              <span className="text-bodyLg text-black">Upload a product image and receive a production-ready tech pack in minutes.</span>
              <Button className="btn-cta w-fit mt-auto" onClick={goToTechPacks}>
                Explore
              </Button>
            </div>
          </div>
        );
      },
      onClick: () => {
        AnalyticsEvent("header_features", "", "click");
        navigate(PATHS.FEATURES + window.location.search);
      },
    },
    {
      title: "Resources",
      renderItem: ({ setShowRenderItem, dropdownTop: top = 96, dropdownLeft: left = 0, onPanelMouseEnter, onPanelMouseLeave }: any) => {
        const goToAmbassadors = () => {
          AnalyticsEvent("header_ambassadors", "", "click");
          navigate(PATHS.AMBASSADORS + window.location.search);
          setShowRenderItem(false);
        };
        const goToAbout = () => {
          AnalyticsEvent("header_about", "", "click");
          navigate(PATHS.ABOUT + window.location.search);
          setShowRenderItem(false);
        };
        const goToAcademy = () => {
          AnalyticsEvent("header_academy", "", "click");
          navigate(PATHS.ACADEMY + window.location.search);
          setShowRenderItem(false);
        };

        return (
          <div
            className="flex flex-col p-4 rounded-2xl gap-1"
            style={{
              position: "fixed",
              top,
              left,
              zIndex: 100,
              background: "rgba(242, 242, 247, 0.72)",
              backdropFilter: "saturate(180%) blur(40px)",
              WebkitBackdropFilter: "saturate(180%) blur(40px)",
            }}
            onMouseEnter={onPanelMouseEnter}
            onMouseLeave={onPanelMouseLeave}
          >
            <a className="header-button !border-b-0 px-3 py-2 hover:bg-black/5 rounded-lg cursor-pointer" onClick={goToAmbassadors}>
              Ambassadors
            </a>
            <a className="header-button !border-b-0 px-3 py-2 hover:bg-black/5 rounded-lg cursor-pointer" onClick={goToAbout}>
              About
            </a>
            <a className="header-button !border-b-0 px-3 py-2 hover:bg-black/5 rounded-lg cursor-pointer" onClick={goToAcademy}>
              Academy
            </a>
          </div>
        );
      },
      onClick: () => {
        AnalyticsEvent("header_resources", "", "click");
      },
    },
    {
      title: "Pricing",
      onClick: () => {
        AnalyticsEvent("header_pricing", "", "click");
        navigate(PATHS.PRICING + window.location.search);
      },
    },
    {
      title: "Docs",
      onClick: () => {
        AnalyticsEvent("header_docs", "", "click");
        window.open(DOCS_URL, "_blank", "noopener,noreferrer");
      },
    },
  ];

  const renderLinks = (isPill = false) => {
    return (
      !isMobile && (
        <div className="flex items-center gap-7 text-[17px] font-normal leading-normal text-grey">
          {HeaderItems.map((item, index) => {
            const isOpen = openDropdownIndex === index;
            if (item.renderButton) {
              return item.renderButton();
            }

            return (
              <div
                className="relative"
                key={`header-button_${index}`}
                ref={isOpen ? dropdownRef : undefined}
                onMouseEnter={(e) => item.renderItem && openDropdown(index, (e.currentTarget as HTMLElement).getBoundingClientRect().left)}
                onMouseLeave={() => item.renderItem && closeDropdown()}
              >
                <a
                  className={clsx("header-button")}
                  onClick={() => {
                    if (!item.renderItem) item.onClick && item.onClick();
                  }}
                >
                  {item.title}
                  {item.Icon}
                </a>
                {item.renderItem &&
                  isOpen &&
                  createPortal(
                    item.renderItem({
                      setShowRenderItem: () => setOpenDropdownIndex(null),
                      isPill,
                      dropdownTop,
                      dropdownLeft,
                      onPanelMouseEnter: () => openDropdown(index),
                      onPanelMouseLeave: () => closeDropdown(),
                    }),
                    document.body
                  )}
              </div>
            );
          })}
        </div>
      )
    );
  };

  const renderCreateButton = () => {
    return (
      !isMobile &&
      location.pathname !== PageType.Create && (
        <Button
          className="btn-primary"
          onClick={() => {
            AnalyticsEvent("create_button_header", "", "click");
            navigate(BASIC_APP_CREATE_URL + "?token=" + getAuthTokenFromLocalStorage() + "&client_id=" + useLocalStorage().getItem(CLIENT_ID_LOCAL_STORAGE_KEY));
          }}
        >
          Create
        </Button>
      )
    );
  };

  const Announcement = () => (
    <div className="relative z-20 flex-center w-full shrink-0 py-2.5 px-4 gap-2.5 bg-green">
      <span className="text-h7-satoshi text-center !text-[15px] !leading-[130%]">Refabric is part of the LVMH Startup Acceleration Program at La Maison des Startups</span>
    </div>
  );

  const renderAuthButtons = (isPill = false) => (
    <div className="flex relative items-center gap-2 flex-shrink-0">
      {isCheckingToken ? (
        <></>
      ) : !isAuthorized ? (
        <>
          {!isMobile && (
            <a href={buildAppUrl("/login")} target="_blank" rel="noopener noreferrer">
              <Button
                className={clsx("btn-secondary no-bg btn-sm whitespace-nowrap hover:!bg-black hover:!text-white", isPill && "!rounded-full !px-5", presentationMode && "!border-white/40 !text-white")}
              >
                {" "}
                Sign In
              </Button>
            </a>
          )}
          {location.pathname !== PATHS.PRICING && (
            <Button
              className={clsx("btn-primary btn-sm whitespace-nowrap flex-shrink-0", isPill && "!rounded-full !px-5")}
              onClick={() => {
                AnalyticsEvent("header_tryforfree", "", "click");
                navigate(PATHS.PRICING + window.location.search);
              }}
            >
              Try for Free
            </Button>
          )}
          {isMobile && <IconMenu className="text-black w-6 h-6 flex-shrink-0" onClick={() => setShowMobileMenu(true)} />}
        </>
      ) : (
        <>
          {renderCreateButton()}
          {user.username !== "" && (
            <Button
              disabled={isOpen}
              onClick={toggleDropdown}
              className="flex-center text-h5 text-white py-2.5 px-4 rounded-full bg-white bg-opacity-10 border border-white border-opacity-10 uppercase"
            >
              {user?.username?.slice(0, 1)}
            </Button>
          )}
          <div className="absolute top-[56px] right-0">{isOpen && <AvatarMenu setIsOpen={setIsOpen} />}</div>
        </>
      )}
    </div>
  );

  return (
    <>
      {/* Announcement — scrolls away with the page */}
      <Announcement />
      <AnnouncementModal />

      {/* Webinar banner — hidden once scrolled (as before) */}
      <div className={clsx("transition-opacity duration-300", scrolled ? "pointer-events-none opacity-0" : "opacity-100")}>
        <WebinarModal />
      </div>

      {isMobile && showMobileMenu && <MobileHeaderMenu show={showMobileMenu} headerItems={HeaderItems} onClose={() => setShowMobileMenu(false)} />}

      {/* Header bar — full size, sticky; solid white at the top, blurred glass once scrolled, dark glass in presentation mode */}
      <div
        ref={headerRef}
        className={clsx("sticky top-0 z-50 w-full border-b border-grey-light", presentationMode && "header-dark", presentationInstant && "header-instant")}
        style={{
          transition: presentationInstant ? "none" : "background-color 0.3s ease, border-color 0.3s ease",
          backgroundColor: presentationMode ? "rgba(28, 28, 28, 0.7)" : scrolled ? "rgba(242, 242, 247, 0.72)" : "#ffffff",
          borderBottomColor: presentationMode ? "rgba(255, 255, 255, 0.1)" : undefined,
          backdropFilter: "saturate(180%) blur(40px)",
          WebkitBackdropFilter: "saturate(180%) blur(40px)",
        }}
      >
        <div className="flex h-full items-center w-full py-3 px-[15px] lg:px-20 max-w-[1440px] mx-auto">
          <div className="flex items-center flex-1">
            <LogoRefabric
              className="refabric-logo cursor-pointer"
              onClick={() => {
                AnalyticsEvent("header_refabric_logo", "", "click");
                if (window.location.pathname === PATHS.HOME) {
                  window.scrollTo({ top: 0, behavior: "smooth" });
                } else {
                  navigate(PATHS.HOME + window.location.search);
                }
              }}
            />
          </div>
          <div className="flex items-center justify-center flex-1">{renderLinks()}</div>
          <div className="flex justify-end relative items-center gap-2.5 flex-1">{renderAuthButtons()}</div>
        </div>
      </div>
    </>
  );
};

export default Header;
